import { useCallback, useMemo, useState } from 'react';
import { MdOutlineFileDownload } from 'react-icons/md';
import { AlexaVisibilityResult } from '@blisspointmedia/bpm-types/dist/AlexaVisibility';
import { Button } from '@Components/Button';
import { Appearance, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { EmptyState } from '@Components/EmptyState';
import { HorizontalBarChart } from '@Components/Charts/HorizontalBarChart/HorizontalBarChart';
import { useSetError } from '@src/redux/modals';
import { toPrettyNumber } from '@src/utils/performance-utils';
import { downloadJSONToCSV, exportMultiSheetToExcel } from '@src/utils/download-utils';
import { equalsIgnoreCase } from '@src/utils/text-utils';
import {
  ATTRIBUTE_COMPARISON_BRANDS_LIMIT,
  TOP_ATTRIBUTES_LIMIT
} from '../alexa-visibility-constants';
import {
  AttributeCount,
  AttributeShare,
  buildAttributeExportRows,
  EMPTY_COMPETITOR_ATTRIBUTES,
  formatAttributeShareLabel,
  getAttributeShares,
  groupBy,
  isSameBrand,
  normalizeAttributeCasing,
  rankCompetitorBrands,
  selectAttributeBrands
} from '../alexa-visibility-utils';
import { UNKNOWN_BRAND_COLOR } from '../alexa-visibility-colors';
import { AttributesPrintView } from './AttributesPrintView';
import { useBrandColor } from '../hooks/useBrandColor';

interface AttributesTabProps {
  results: AlexaVisibilityResult[];
  targetBrand: string;
}

// Row height of the attribute bar charts, and room under the bars for the value axis.
const ATTRIBUTE_ROW_HEIGHT = 24;
const CHART_AXIS_HEIGHT = 40;

interface BrandAttributesEntry {
  brand: string;
  attributes: string[];
}

const buildRawAttributesByBrand = (
  results: AlexaVisibilityResult[],
  targetBrand: string
): Map<string, string[]> => {
  const entries: BrandAttributesEntry[] = results.flatMap(result => {
    const targetEntry: BrandAttributesEntry[] = result.classification?.keyAttributesCited?.length
      ? [{ brand: targetBrand, attributes: result.classification.keyAttributesCited }]
      : [];
    const competitorEntries = Object.entries(
      result.classification?.competitorAttributes ?? EMPTY_COMPETITOR_ATTRIBUTES
    ).map(([brand, attributes]) => ({ brand, attributes }));
    return [...targetEntry, ...competitorEntries];
  });

  const byBrand = new Map<string, string[]>();
  groupBy(entries, entry => entry.brand).forEach((brandEntries, brand) => {
    byBrand.set(
      brand,
      brandEntries.flatMap(entry => entry.attributes)
    );
  });
  return byBrand;
};

interface BrandAttributeChartProps {
  brand: string;
  isTargetBrand: boolean;
  attributes: AttributeCount[];
  color: string;
}

// One brand's most-cited attributes, as wide as the page, with every attribute downloadable.
const BrandAttributeChart = ({
  brand,
  isTargetBrand,
  attributes,
  color
}: BrandAttributeChartProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const setError = useSetError();
  const shares = useMemo(() => getAttributeShares(attributes), [attributes]);
  const totalCitations = attributes.reduce((sum, { count }) => sum + count, 0);
  const shown = shares.slice(0, TOP_ATTRIBUTES_LIMIT);

  const handleDownloadAll = async () => {
    setIsExporting(true);
    try {
      await exportMultiSheetToExcel(
        [{ name: 'Attributes', data: buildAttributeExportRows(attributes) }],
        `AlexaVisibility_Attributes_${brand}`
      );
    } catch (e) {
      setError({
        title: "Couldn't export attributes",
        message: `Failed to export ${brand} attributes to Excel: ${e.message}`,
        reportError: e
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className='aars-chart-card aars-chart-card-wide'>
      <div className='aars-chart-card-header'>
        <div className='aars-chart-card-title'>
          <span className='aars-brand-chip' style={{ borderColor: color }}>
            {brand}
          </span>
          {isTargetBrand && <span className='aars-target-brand-tag'>Target</span>}
          <span className='aars-chart-card-subtitle'>
            {attributes.length} unique attributes · {toPrettyNumber(totalCitations)} total citations
            · showing top {shown.length}
          </span>
        </div>
        <Button
          size={Size.SM}
          appearance={Appearance.OUTLINED}
          leadingElement={<MdOutlineFileDownload />}
          onClick={handleDownloadAll}
          loading={isExporting}
        >
          Download all ({attributes.length})
        </Button>
      </div>
      <HorizontalBarChart<AttributeShare>
        data={shown}
        barKey='count'
        yAxisKey='attribute'
        yAxisLabel='Citations'
        barFillColor={color}
        getBarLabel={formatAttributeShareLabel}
        pageLimit={TOP_ATTRIBUTES_LIMIT}
        fixedHeight
        height={shown.length * ATTRIBUTE_ROW_HEIGHT + CHART_AXIS_HEIGHT}
      />
    </div>
  );
};

interface CrossBrandAttributeComparisonProps {
  brands: string[];
  rawAttributesByBrand: Map<string, string[]>;
  attributeOptions: string[];
  getBrandColor: (brand: string) => string;
}

interface BrandAttributeCount {
  brand: string;
  count: number;
}

const CrossBrandAttributeComparison = ({
  brands,
  rawAttributesByBrand,
  attributeOptions,
  getBrandColor
}: CrossBrandAttributeComparisonProps) => {
  const [selectedAttribute, setSelectedAttribute] = useState<string>(attributeOptions[0] ?? '');

  const comparisonData = useMemo<BrandAttributeCount[]>(() => {
    if (!selectedAttribute) return [];
    return brands
      .map(brand => ({
        brand,
        count: (rawAttributesByBrand.get(brand) ?? []).filter(attribute =>
          equalsIgnoreCase(attribute, selectedAttribute)
        ).length
      }))
      .filter(item => item.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, ATTRIBUTE_COMPARISON_BRANDS_LIMIT);
  }, [brands, rawAttributesByBrand, selectedAttribute]);

  if (attributeOptions.length === 0) {
    return null;
  }

  return (
    <div className='aars-chart-card aars-cross-brand-comparison'>
      <div className='aars-chart-card-header'>
        <div className='aars-chart-card-title'>Attribute comparison by brand</div>
      </div>
      <div className='aars-chart-card-description'>
        Select an attribute to see how many times each brand was cited for it
      </div>
      <Dropdown
        size={Size.LG}
        searchable
        value={selectedAttribute}
        options={attributeOptions.map(attribute => ({ label: attribute, value: attribute }))}
        onChange={value => setSelectedAttribute(value ?? '')}
      />
      {comparisonData.length === 0 ? (
        <EmptyState title='No brands were cited for this attribute' />
      ) : (
        <HorizontalBarChart<BrandAttributeCount>
          data={comparisonData}
          barKey='count'
          yAxisKey='brand'
          yAxisLabel='Citations'
          barFillColor={UNKNOWN_BRAND_COLOR}
          getBarColor={row => getBrandColor(row.brand)}
          getBarLabel={row => toPrettyNumber(row.count)}
          pageLimit={comparisonData.length}
          fixedHeight
          height={comparisonData.length * ATTRIBUTE_ROW_HEIGHT + CHART_AXIS_HEIGHT}
        />
      )}
    </div>
  );
};

export const AttributesTab = ({ results, targetBrand }: AttributesTabProps) => {
  const rawAttributesByBrand = useMemo(
    () => buildRawAttributesByBrand(results, targetBrand),
    [results, targetBrand]
  );

  const normalizedByBrand = useMemo(() => {
    const map = new Map<string, AttributeCount[]>();
    rawAttributesByBrand.forEach((attributes, brand) => {
      map.set(brand, normalizeAttributeCasing(attributes));
    });
    return map;
  }, [rawAttributesByBrand]);

  // The target brand leads, then the competitors Alexa mentions most (the same ones, in the same
  // order, as the Query Overview), so a brand looks the same on every tab.
  const brands = useMemo(() => {
    const citationsByBrand = new Map(
      Array.from(rawAttributesByBrand.entries()).map(([brand, attributes]) => [
        brand,
        attributes.length
      ])
    );
    return selectAttributeBrands(
      citationsByBrand,
      rankCompetitorBrands(results, targetBrand),
      targetBrand
    );
  }, [rawAttributesByBrand, results, targetBrand]);

  // Brands with attributes that Alexa never listed as mentioned still get their own color.
  const attributeBrands = useMemo(
    () =>
      Array.from(rawAttributesByBrand.keys()).sort(
        (a, b) =>
          (rawAttributesByBrand.get(b)?.length ?? 0) - (rawAttributesByBrand.get(a)?.length ?? 0)
      ),
    [rawAttributesByBrand]
  );
  const getBrandColor = useBrandColor(results, targetBrand, attributeBrands);

  const attributeOptions = useMemo(
    () =>
      normalizeAttributeCasing(Array.from(rawAttributesByBrand.values()).flat()).map(
        a => a.attribute
      ),
    [rawAttributesByBrand]
  );

  const [isPrintViewOpen, setIsPrintViewOpen] = useState(false);
  const closePrintView = useCallback(() => setIsPrintViewOpen(false), []);

  const handleDownloadCsv = () => {
    const rows = attributeBrands.flatMap(brand =>
      (normalizedByBrand.get(brand) ?? []).map(({ attribute, count }) => ({
        brand,
        attribute,
        count
      }))
    );
    downloadJSONToCSV(rows, `AlexaVisibility_Attributes_${targetBrand}`);
  };

  if (brands.length === 0) {
    return (
      <EmptyState
        title='No attribute citations yet'
        subtitle='Attributes cited for this brand and its competitors will appear here once results are in.'
      />
    );
  }

  if (isPrintViewOpen) {
    return (
      <AttributesPrintView
        targetBrand={targetBrand}
        onClose={closePrintView}
        brands={brands.map(brand => ({
          brand,
          isTargetBrand: isSameBrand(brand, targetBrand),
          color: getBrandColor(brand),
          attributes: normalizedByBrand.get(brand) ?? []
        }))}
      />
    );
  }

  return (
    <div className='attributes-tab'>
      <div className='aars-tab-actions'>
        <div className='aars-tab-header'>
          <div className='aars-tab-title'>Product attributes overview</div>
          <div className='aars-tab-description'>
            A brand-by-brand breakdown of the product attributes Alexa associates with each product,
            revealing what features drive citations, where {targetBrand} leads, and where
            competitors hold the advantage.
          </div>
        </div>
        <div className='aars-export-buttons'>
          <Button
            size={Size.SM}
            appearance={Appearance.OUTLINED}
            leadingElement={<MdOutlineFileDownload />}
            onClick={() => setIsPrintViewOpen(true)}
          >
            Export PDF
          </Button>
          <Button
            size={Size.SM}
            appearance={Appearance.OUTLINED}
            leadingElement={<MdOutlineFileDownload />}
            onClick={handleDownloadCsv}
          >
            Export CSV
          </Button>
        </div>
      </div>
      <CrossBrandAttributeComparison
        brands={attributeBrands}
        rawAttributesByBrand={rawAttributesByBrand}
        attributeOptions={attributeOptions}
        getBrandColor={getBrandColor}
      />
      <div className='aars-chart-grid'>
        {brands.map(brand => (
          <BrandAttributeChart
            key={brand}
            brand={brand}
            isTargetBrand={isSameBrand(brand, targetBrand)}
            attributes={normalizedByBrand.get(brand) ?? []}
            color={getBrandColor(brand)}
          />
        ))}
      </div>
    </div>
  );
};
